import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const localizer = momentLocalizer(moment);

const EVENT_COLORS = {
  job: '#3B82F6',
  task: '#F59E0B',
  follow_up: '#10B981',
  reminder: '#EF4444'
};

const EVENT_LABELS = {
  job: 'Job',
  task: 'Task',
  follow_up: 'Follow-up',
  reminder: 'Reminder'
};

// Helper: convert datetime-local string to ISO string
const toISO = (dtLocal) => {
  if (!dtLocal) return null;
  // datetime-local format: "2026-04-20T10:00" — treat as local time
  return new Date(dtLocal)?.toISOString();
};

// Helper: format a Date or ISO string to datetime-local input value
const toDatetimeLocal = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d)) return '';
  const pad = (n) => String(n)?.padStart(2, '0');
  return `${d?.getFullYear()}-${pad(d?.getMonth() + 1)}-${pad(d?.getDate())}T${pad(d?.getHours())}:${pad(d?.getMinutes())}`;
};

const EMPTY_EVENT = {
  title: '', description: '', start_datetime: '', end_datetime: '',
  event_type: 'reminder', color: '#EF4444',
  contact_name: '', contact_phone: '', contact_email: ''
};

const CalendarPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newEvent, setNewEvent] = useState(EMPTY_EVENT);
  const { profile } = useAuth();

  const fetchEvents = useCallback(async () => {
    if (!profile?.org_id) return;
    setLoading(true);
    try {
      const { data: calData, error: calError } = await supabase
        ?.from('calendar_events')
        ?.select('*, job:jobs(title, status, service_type, location, custom_fields, tech_id, move_type, cubic_feet, miles)')
        ?.eq('org_id', profile?.org_id)
        ?.is('deleted_at', null)
        ?.order('start_datetime', { ascending: true });
      if (calError) throw calError;

      const calJobIds = new Set((calData || [])?.filter(e => e?.job_id)?.map(e => e?.job_id));

      const { data: jobsData } = await supabase
        ?.from('jobs')
        ?.select('id, title, service_type, status, scheduled_date, scheduled_time, duration_hours, description, location, custom_fields, tech_id, move_type, cubic_feet, miles')
        ?.eq('org_id', profile?.org_id)
        ?.is('deleted_at', null)
        ?.not('scheduled_date', 'is', null);

      const allTechIds = new Set();
      (calData || [])?.forEach(ev => { if (ev?.job?.tech_id) allTechIds?.add(ev?.job?.tech_id); });
      (jobsData || [])?.forEach(j => { if (j?.tech_id) allTechIds?.add(j?.tech_id); });

      let techMap = {};
      if (allTechIds?.size > 0) {
        const { data: techData } = await supabase
          ?.from('technicians')
          ?.select('id, name, role')
          ?.in('id', Array.from(allTechIds));
        (techData || [])?.forEach(t => { techMap[t?.id] = t; });
      }

      const calMapped = (calData || [])?.map(ev => ({
        id: ev?.id,
        title: ev?.title,
        start: new Date(ev.start_datetime),
        end: new Date(ev.end_datetime),
        resource: {
          ...ev,
          _isJobEvent: false,
          _techName: ev?.job?.tech_id ? techMap?.[ev?.job?.tech_id]?.name : null,
          _techRole: ev?.job?.tech_id ? techMap?.[ev?.job?.tech_id]?.role : null,
        },
        color: EVENT_COLORS?.[ev?.event_type] || '#3B82F6'
      }));

      const jobMapped = (jobsData || [])
        ?.filter(j => !calJobIds?.has(j?.id))
        ?.map(j => {
          const [year, month, day] = (j?.scheduled_date || '')?.split('-')?.map(Number);
          const timeStr = j?.scheduled_time
            ? (j?.scheduled_time?.length >= 5 ? j?.scheduled_time?.substring(0, 5) : j?.scheduled_time)
            : '09:00';
          const [hours, minutes] = timeStr?.split(':')?.map(Number);
          const startDate = new Date(year, month - 1, day, hours, minutes, 0);
          const endDate = new Date(startDate.getTime() + (Number(j?.duration_hours) || 1) * 3600000);
          return {
            id: `job-${j?.id}`,
            title: j?.title,
            start: startDate,
            end: endDate,
            resource: {
              id: `job-${j?.id}`,
              job_id: j?.id,
              title: j?.title,
              description: j?.description || '',
              start_datetime: startDate?.toISOString(),
              end_datetime: endDate?.toISOString(),
              event_type: 'job',
              _isJobEvent: true,
              job: { title: j?.title, status: j?.status, service_type: j?.service_type, location: j?.location, custom_fields: j?.custom_fields, tech_id: j?.tech_id, move_type: j?.move_type, cubic_feet: j?.cubic_feet, miles: j?.miles },
              _techName: j?.tech_id ? techMap?.[j?.tech_id]?.name : null,
              _techRole: j?.tech_id ? techMap?.[j?.tech_id]?.role : null,
            },
            color: EVENT_COLORS?.job
          };
        });

      setEvents([...calMapped, ...jobMapped]);
    } catch (err) {
      toast?.error('Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  }, [profile?.org_id]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Called when user clicks/drags a slot on the calendar
  const handleSelectSlot = ({ start, end }) => {
    setNewEvent({
      ...EMPTY_EVENT,
      start_datetime: toDatetimeLocal(start),
      end_datetime: toDatetimeLocal(end && end > start ? end : new Date(start.getTime() + 3600000)),
    });
    setIsAddModalOpen(true);
  };

  const handleAddEvent = async () => {
    if (!newEvent?.title?.trim() || !newEvent?.start_datetime) return;
    setSaving(true);
    try {
      const startISO = toISO(newEvent?.start_datetime);
      const endISO = newEvent?.end_datetime
        ? toISO(newEvent?.end_datetime)
        : new Date(new Date(newEvent.start_datetime).getTime() + 3600000)?.toISOString();

      let descParts = [];
      if (newEvent?.description?.trim()) descParts?.push(newEvent?.description?.trim());
      if (newEvent?.contact_name?.trim()) descParts?.push(`Contact: ${newEvent?.contact_name?.trim()}`);
      if (newEvent?.contact_phone?.trim()) descParts?.push(`Phone: ${newEvent?.contact_phone?.trim()}`);
      if (newEvent?.contact_email?.trim()) descParts?.push(`Email: ${newEvent?.contact_email?.trim()}`);

      const { error } = await supabase?.from('calendar_events')?.insert({
        org_id: profile?.org_id,
        title: newEvent?.title,
        description: descParts?.join('\n') || null,
        start_datetime: startISO,
        end_datetime: endISO,
        event_type: newEvent?.event_type,
        color: EVENT_COLORS?.[newEvent?.event_type] || '#3B82F6'
      });
      if (error) throw error;
      toast?.success('Event added!');
      setNewEvent(EMPTY_EVENT);
      setIsAddModalOpen(false);
      fetchEvents();
    } catch (err) {
      toast?.error('Failed to add event');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async (event) => {
    // Job-derived events (not stored in calendar_events) cannot be deleted from here
    if (event?._isJobEvent) {
      toast('Job events can be managed from the Jobs page', { icon: 'ℹ️' });
      return;
    }
    try {
      const { error } = await supabase
        ?.from('calendar_events')
        ?.update({ deleted_at: new Date()?.toISOString() })
        ?.eq('id', event?.id);
      if (error) throw error;
      toast?.success('Event removed');
      setSelectedEvent(null);
      fetchEvents();
    } catch (err) {
      toast?.error('Failed to delete event');
    }
  };

  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: event?.color,
      borderRadius: '6px',
      border: 'none',
      color: 'white',
      fontSize: '12px',
      padding: '2px 6px'
    }
  });

  // Parse contact info from description
  const parseContactFromDescription = (desc) => {
    if (!desc) return {};
    const lines = desc?.split('\n');
    const contact = {};
    lines?.forEach(line => {
      if (line?.startsWith('Contact: ')) contact.name = line?.replace('Contact: ', '');
      else if (line?.startsWith('Phone: ')) contact.phone = line?.replace('Phone: ', '');
      else if (line?.startsWith('Email: ')) contact.email = line?.replace('Email: ', '');
    });
    return contact;
  };

  const getDescriptionText = (desc) => {
    if (!desc) return '';
    return desc?.split('\n')?.filter(l => !l?.startsWith('Contact: ') && !l?.startsWith('Phone: ') && !l?.startsWith('Email: '))?.join('\n');
  };

  return (
    <>
      <Helmet><title>Calendar - Wilcart CRM</title></Helmet>
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} isSidebarOpen={isSidebarOpen} />
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <main className="lg:ml-64 pt-24 lg:pt-28">
          <div className="p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Calendar</h1>
                <p className="text-muted-foreground mt-1">Jobs, tasks, and events</p>
              </div>
              <div className="flex items-center space-x-3 mt-4 sm:mt-0">
                <div className="hidden md:flex items-center space-x-3">
                  {Object.entries(EVENT_COLORS)?.map(([type, color]) => (
                    <div key={type} className="flex items-center space-x-1">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                      <span className="text-xs text-muted-foreground">{EVENT_LABELS?.[type]}</span>
                    </div>
                  ))}
                </div>
                <Button onClick={() => { setNewEvent(EMPTY_EVENT); setIsAddModalOpen(true); }}>
                  <Icon name="Plus" size={16} className="mr-2" />
                  Add Event
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="h-96 bg-muted animate-pulse rounded-xl" />
            ) : (
              <div className="bg-card border border-border rounded-xl p-4" style={{ height: '600px' }}>
                <BigCalendar
                  localizer={localizer}
                  events={events}
                  startAccessor="start"
                  endAccessor="end"
                  view={currentView}
                  onView={setCurrentView}
                  date={currentDate}
                  onNavigate={setCurrentDate}
                  eventPropGetter={eventStyleGetter}
                  onSelectEvent={(event) => setSelectedEvent(event?.resource)}
                  onSelectSlot={handleSelectSlot}
                  selectable
                  style={{ height: '100%' }}
                  views={['month', 'week', 'day']}
                  popup
                />
              </div>
            )}
          </div>
        </main>

        {/* Event Detail Popup */}
        {selectedEvent && (() => {
          const contactInfo = parseContactFromDescription(selectedEvent?.description);
          const descText = getDescriptionText(selectedEvent?.description);
          return (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
              <div className="bg-card border border-border rounded-xl p-6 w-full max-w-sm">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: EVENT_COLORS?.[selectedEvent?.event_type] || '#3B82F6' }} />
                      <span className="text-xs font-medium text-muted-foreground uppercase">{EVENT_LABELS?.[selectedEvent?.event_type]}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">{selectedEvent?.title}</h3>
                  </div>
                  <button onClick={() => setSelectedEvent(null)} className="text-muted-foreground hover:text-foreground">
                    <Icon name="X" size={20} />
                  </button>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2 text-muted-foreground">
                    <Icon name="Clock" size={14} />
                    <span>{new Date(selectedEvent.start_datetime)?.toLocaleString()}</span>
                  </div>
                  {descText && (
                    <p className="text-foreground">{descText}</p>
                  )}

                  {/* Contact info section */}
                  {(contactInfo?.name || contactInfo?.phone || contactInfo?.email) && (
                    <div className="bg-muted/50 rounded-lg p-3 space-y-1.5">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Contact</p>
                      {contactInfo?.name && (
                        <div className="flex items-center space-x-2">
                          <Icon name="User" size={13} className="text-muted-foreground shrink-0" />
                          <span className="text-sm text-foreground font-medium">{contactInfo?.name}</span>
                        </div>
                      )}
                      {contactInfo?.phone && (
                        <div className="flex items-center space-x-2">
                          <Icon name="Phone" size={13} className="text-muted-foreground shrink-0" />
                          <span className="text-sm text-foreground">{contactInfo?.phone}</span>
                        </div>
                      )}
                      {contactInfo?.email && (
                        <div className="flex items-center space-x-2">
                          <Icon name="Mail" size={13} className="text-muted-foreground shrink-0" />
                          <span className="text-sm text-foreground">{contactInfo?.email}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedEvent?.job && (
                    <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 space-y-2">
                      <p className="text-xs text-blue-700 dark:text-blue-400 font-medium">Linked Job: {selectedEvent?.job?.title}</p>
                      <p className="text-xs text-blue-600 dark:text-blue-300">{selectedEvent?.job?.service_type} · {selectedEvent?.job?.status}</p>

                      {(() => {
                        const svcType = selectedEvent?.job?.service_type?.toLowerCase() || '';
                        const isMoving = svcType?.includes('moving') || svcType?.includes('move') || svcType === 'local_move' || svcType === 'long_distance' || svcType === 'commercial_move';
                        const cf = selectedEvent?.job?.custom_fields || {};
                        const pickupAddr = cf?.pickup_address || cf?.move_from || '';
                        const deliveryAddr = cf?.delivery_address || cf?.move_to || '';
                        const serviceAddr = selectedEvent?.job?.location || '';
                        const moveType = selectedEvent?.job?.move_type;
                        const cubicFeet = selectedEvent?.job?.cubic_feet;
                        const miles = selectedEvent?.job?.miles;

                        if (isMoving) {
                          return (
                            <div className="space-y-1.5 pt-1 border-t border-blue-200 dark:border-blue-800">
                              {pickupAddr && (
                                <div className="flex items-start space-x-1.5">
                                  <Icon name="MapPin" size={12} className="text-green-500 mt-0.5 shrink-0" />
                                  <div>
                                    <p className="text-xs text-muted-foreground font-medium">From</p>
                                    <p className="text-xs text-foreground">{pickupAddr}</p>
                                  </div>
                                </div>
                              )}
                              {deliveryAddr && (
                                <div className="flex items-start space-x-1.5">
                                  <Icon name="MapPin" size={12} className="text-red-500 mt-0.5 shrink-0" />
                                  <div>
                                    <p className="text-xs text-muted-foreground font-medium">To</p>
                                    <p className="text-xs text-foreground">{deliveryAddr}</p>
                                  </div>
                                </div>
                              )}
                              {(moveType === 'long_distance' || (!moveType && (cubicFeet || miles))) && (
                                <div className="flex items-center space-x-3 pt-1">
                                  {cubicFeet && (
                                    <div className="flex items-center space-x-1">
                                      <Icon name="Package" size={11} className="text-muted-foreground" />
                                      <span className="text-xs text-foreground font-medium">{cubicFeet} CF</span>
                                    </div>
                                  )}
                                  {miles && (
                                    <div className="flex items-center space-x-1">
                                      <Icon name="Route" size={11} className="text-muted-foreground" />
                                      <span className="text-xs text-foreground font-medium">{miles} mi</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        } else if (serviceAddr) {
                          return (
                            <div className="flex items-start space-x-1.5 pt-1 border-t border-blue-200 dark:border-blue-800">
                              <Icon name="MapPin" size={12} className="text-blue-500 mt-0.5 shrink-0" />
                              <div>
                                <p className="text-xs text-muted-foreground font-medium">Address</p>
                                <p className="text-xs text-foreground">{serviceAddr}</p>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {selectedEvent?._techName && (
                        <div className="flex items-center space-x-1.5 pt-1 border-t border-blue-200 dark:border-blue-800">
                          <Icon name="User" size={12} className="text-blue-500 shrink-0" />
                          <div>
                            <p className="text-xs text-muted-foreground font-medium">Assigned</p>
                            <p className="text-xs text-foreground font-medium">{selectedEvent?._techName}{selectedEvent?._techRole ? ` · ${selectedEvent?._techRole}` : ''}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex space-x-2 mt-4">
                  <Button variant="outline" className="flex-1" onClick={() => setSelectedEvent(null)}>Close</Button>
                  {selectedEvent?._isJobEvent ? (
                    <Button variant="outline" size="sm" onClick={() => { toast('Manage this job from the Jobs page', { icon: 'ℹ️' }); setSelectedEvent(null); }}>
                      <Icon name="ExternalLink" size={14} className="mr-1" />
                      View Job
                    </Button>
                  ) : (
                    <Button variant="destructive" size="sm" onClick={() => handleDeleteEvent(selectedEvent)}>
                      <Icon name="Trash2" size={14} className="mr-1" />
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Add Event Modal */}
        {isAddModalOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground">Add Event</h2>
                <button onClick={() => setIsAddModalOpen(false)} className="text-muted-foreground hover:text-foreground">
                  <Icon name="X" size={20} />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Title *</label>
                  <input type="text" value={newEvent?.title} onChange={e => setNewEvent(p => ({ ...p, title: e?.target?.value }))}
                    placeholder="Event title"
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Type</label>
                  <select value={newEvent?.event_type} onChange={e => setNewEvent(p => ({ ...p, event_type: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background">
                    <option value="job">Job</option>
                    <option value="task">Task</option>
                    <option value="follow_up">Follow-up</option>
                    <option value="reminder">Reminder</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Start *</label>
                    <input type="datetime-local" value={newEvent?.start_datetime} onChange={e => setNewEvent(p => ({ ...p, start_datetime: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">End</label>
                    <input type="datetime-local" value={newEvent?.end_datetime} onChange={e => setNewEvent(p => ({ ...p, end_datetime: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Description</label>
                  <textarea value={newEvent?.description} onChange={e => setNewEvent(p => ({ ...p, description: e?.target?.value }))}
                    rows={2} placeholder="Optional notes..."
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background resize-none" />
                </div>

                {/* Contact Info Section */}
                <div className="border border-border rounded-lg p-3 space-y-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center space-x-1">
                    <Icon name="User" size={13} />
                    <span>Contact Info (optional)</span>
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Contact Name</label>
                    <input type="text" value={newEvent?.contact_name}
                      onChange={e => setNewEvent(p => ({ ...p, contact_name: e?.target?.value }))}
                      placeholder="Client name"
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Phone</label>
                      <input type="tel" value={newEvent?.contact_phone}
                        onChange={e => setNewEvent(p => ({ ...p, contact_phone: e?.target?.value }))}
                        placeholder="+1 (555) 000-0000"
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Email</label>
                      <input type="email" value={newEvent?.contact_email}
                        onChange={e => setNewEvent(p => ({ ...p, contact_email: e?.target?.value }))}
                        placeholder="client@email.com"
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setIsAddModalOpen(false)}>Cancel</Button>
                <Button className="flex-1" onClick={handleAddEvent} loading={saving} disabled={saving || !newEvent?.title?.trim() || !newEvent?.start_datetime}>
                  {saving ? 'Adding...' : 'Add Event'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CalendarPage;
